<template>
  <Toast />
  <ConfirmDialog />

  <!-- PANTALLA CAJA CERRADA -->
  <div
    v-if="!verificando && !cajaAbierta"
    class="flex flex-column align-items-center justify-content-center min-h-screen"
    style="background: #f8fafc"
  >
    <div
      class="surface-card p-6 shadow-8 border-round-2xl text-center"
      style="max-width: 400px; width: 100%"
    >
      <div
        class="bg-orange-100 border-circle inline-flex align-items-center justify-content-center mb-4"
        style="width: 100px; height: 100px"
      >
        <i class="pi pi-lock text-6xl text-orange-600"></i>
      </div>
      <h2 class="text-900 font-bold text-2xl mb-2">Caja Cerrada</h2>
      <p class="text-600 mb-5 line-height-3">
        El sistema está bloqueado hasta que inicies turno.
      </p>
      <Button
        label="Abrir Caja"
        icon="pi pi-key"
        class="p-button-rounded p-button-lg w-full font-bold border-none"
        style="background: linear-gradient(135deg, #f97316, #ea580c)"
        @click="$router.push({ name: 'caja' })"
      />
    </div>
  </div>

  <!-- PLANO NORMAL -->
  <div v-if="!verificando && cajaAbierta">
    <div class="plano-visual-container">
      <div class="plano-header">
        <div class="header-left">
          <h1 class="plano-title">Plano Visual de Mesas</h1>
          <div class="estadisticas">
            <span class="stat-item">
              <span class="stat-dot stat-libre"></span>
              Libres: <b>{{ estadisticas.disponibles }}</b>
            </span>
            <span class="stat-item">
              <span class="stat-dot stat-ocupada"></span>
              Ocupadas: <b>{{ estadisticas.ocupadas }}</b>
            </span>
            <span class="stat-item">
              <span class="stat-dot stat-para-llevar"></span>
              Para Llevar: <b>{{ estadisticasParaLlevar.paraLlevar }}</b>
            </span>
            <span class="stat-item">
              <span class="stat-dot stat-yango"></span>
              Yango: <b>{{ estadisticasParaLlevar.yango }}</b>
            </span>
          </div>
        </div>

        <div class="header-right">
          <Button
            v-if="'Mesa Editar' in auth.user.permissions && !modoEditar"
            icon="pi pi-pencil"
            label="Editar Layout"
            class="p-button-warning"
            @click="activarModoEditar"
          />

          <Button
            v-if="modoEditar"
            icon="pi pi-save"
            label="Guardar Layout"
            class="p-button-success"
            :loading="guardando"
            @click="guardarPosiciones"
          />

          <Button
            v-if="modoEditar"
            icon="pi pi-times"
            label="Cancelar"
            class="p-button-secondary"
            @click="cancelarEdicion"
          />

          <Button
            v-if="!modoEditar"
            icon="pi pi-refresh"
            class="p-button-info"
            @click="cargarTodo"
            :loading="cargando"
            v-tooltip.bottom="'Actualizar'"
          />

          <Button
            v-if="!modoEditar"
            label="Vista Lista"
            icon="pi pi-list"
            class="p-button-outlined"
            @click="$router.push({ name: 'restaurante_mesas' })"
          />

          <Button
            v-if="!modoEditar"
            class="btn-takeout"
            @click="$router.push({ name: 'restaurante_para_llevar' })"
          >
            <div class="flex align-items-center gap-2 text-white font-bold text-sm">
              <i class="pi pi-shopping-bag"></i>
              <span class="hidden sm:inline">Para Llevar</span>
            </div>
          </Button>

          <Button
            v-if="!modoEditar"
            class="btn-yango"
            @click="$router.push({ name: 'restaurante_yango' })"
          >
            <div class="flex align-items-center gap-2 text-white font-bold text-sm">
              <i class="pi pi-car"></i>
              <span class="hidden sm:inline">Yango</span>
            </div>
          </Button>
        </div>
      </div>

      <div v-if="modoEditar" class="modo-edicion-banner">
        <i class="pi pi-info-circle"></i>
        <span
          >Modo Edición: Arrastra las mesas libremente dentro de cada zona. Haz
          clic en "Guardar Layout" cuando termines.</span
        >
      </div>

      <!-- Sección de Pedidos Para Llevar -->
      <div
        v-if="
          !modoEditar &&
          pedidosParaLlevar.filter((p) => p.tipo === 'para_llevar').length > 0
        "
        class="seccion-para-llevar"
      >
        <h3 class="seccion-titulo">
          <i class="pi pi-shopping-bag"></i>
          Pedidos Para Llevar Activos
        </h3>
        <div class="tickets-grid">
          <div
            v-for="pedido in pedidosParaLlevar.filter(
              (p) => p.tipo === 'para_llevar',
            )"
            :key="pedido.id"
            class="ticket-compacto"
            :class="getTicketClass(pedido.estado)"
            @click="abrirPedidoParaLlevar(pedido)"
          >
            <div class="ticket-top">
              <div class="ticket-numero">#{{ pedido.id }}</div>
              <Badge
                :value="getEstadoTexto(pedido.estado)"
                :severity="getEstadoSeverity(pedido.estado)"
                class="text-xs"
              />
            </div>

            <div class="ticket-info">
              <div class="ticket-monto">
                Bs. {{ formatCurrency(pedido.total) }}
              </div>
              <div class="ticket-meta">
                <span
                  v-tooltip.top="getResumenProductos(pedido.items)"
                  class="cursor-pointer"
                >
                  <i class="pi pi-box text-xs"></i>
                  {{ pedido.items?.length || 0 }}
                </span>
                <span>
                  <i class="pi pi-clock text-xs"></i>
                  {{ tiempoTranscurrido(pedido.created_at) }}
                </span>
              </div>
            </div>

            <div class="ticket-cliente">
              <i class="pi pi-user text-xs"></i>
              {{ pedido.cliente?.nombre || "General" }}
            </div>

            <div class="ticket-action" @click.stop>
              <Button
                v-if="pedido.venta_id"
                label="ENTREGAR"
                icon="pi pi-check-circle"
                class="p-button-sm p-button-success font-bold w-full"
                @click="confirmarEntregar(pedido)"
              />
              <Button
                v-else
                label="FALTA PAGAR"
                icon="pi pi-exclamation-triangle"
                class="p-button-sm p-button-warning font-bold w-full"
                @click="abrirPedidoParaLlevar(pedido)"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- Sección de Pedidos Yango -->
      <div v-if="!modoEditar && pedidosYango.length > 0" class="seccion-yango">
        <h3 class="seccion-titulo seccion-titulo-yango">
          <i class="pi pi-car"></i>
          Pedidos Yango Activos
        </h3>
        <div class="tickets-grid">
          <div
            v-for="pedido in pedidosYango"
            :key="pedido.id"
            class="ticket-compacto ticket-yango"
            :class="getTicketClass(pedido.estado)"
            @click="abrirPedidoParaLlevar(pedido)"
          >
            <div class="ticket-top">
              <div class="ticket-numero flex align-items-center gap-1">
                <i class="pi pi-car text-lg"></i>
                #{{ pedido.id }}
              </div>
              <Badge
                :value="getEstadoTexto(pedido.estado)"
                :severity="getEstadoSeverity(pedido.estado)"
                class="text-xs"
              />
            </div>

            <div class="ticket-info">
              <div class="ticket-monto">
                Bs. {{ formatCurrency(pedido.total) }}
              </div>
              <div class="ticket-meta">
                <span
                  v-tooltip.top="getResumenProductos(pedido.items)"
                  class="cursor-pointer"
                >
                  <i class="pi pi-box text-xs"></i>
                  {{ pedido.items?.length || 0 }}
                </span>
                <span>
                  <i class="pi pi-clock text-xs"></i>
                  {{ tiempoTranscurrido(pedido.created_at) }}
                </span>
              </div>
            </div>

            <div class="ticket-cliente">
              <i class="pi pi-user text-xs"></i>
              {{ pedido.cliente?.nombre || "General" }}
            </div>

            <div class="ticket-action" @click.stop>
              <Button
                label="ENTREGAR"
                icon="pi pi-check-circle"
                class="p-button-sm p-button-danger font-bold w-full"
                @click="confirmarEntregarYango(pedido)"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- Canvas de Mesas con Fondo Oscuro -->
      <div class="plano-canvas" ref="canvas">
        <div v-for="zona in zonasUnicas" :key="zona" class="zona-section">
          <div class="zona-header">
            {{ zona }}
          </div>

          <div class="zona-canvas">
            <div
              v-for="mesa in mesasPorZona(zona)"
              :key="mesa.id"
              :data-mesa-id="mesa.id"
              class="mesa-item"
              :class="[getEstadoClass(mesa.estado), { draggable: modoEditar }]"
              :style="{
                left: (mesa.posicion_x || getDefaultXZona(mesa, zona)) + 'px',
                top: (mesa.posicion_y || getDefaultYZona(mesa, zona)) + 'px',
              }"
              @click="!modoEditar && accionMesa(mesa)"
              v-tooltip.top="!modoEditar ? getTooltipItems(mesa) : null"
            >
              <div class="chairs-row top">
                <div
                  v-for="n in getSillasTop(mesa.capacidad)"
                  :key="'t' + n"
                  class="chair"
                ></div>
              </div>

              <div class="table-body">
                <div
                  v-if="mesa.pedido_id && mesa.estado === 2"
                  class="pedido-badge"
                >
                  #{{ mesa.nro_pedido || mesa.pedido_id }}
                </div>

                <span class="mesa-numero">{{ mesa.numero }}</span>

                <i
                  v-if="mesa.estado === 2"
                  class="pi pi-user mesa-icon-ocupada"
                ></i>

                <div
                  v-if="mesa.pedido_id && mesa.estado === 2"
                  class="price-tag"
                >
                  <small>Bs.</small> {{ formatCurrency(mesa.pedido_total) }}
                </div>
              </div>

              <div class="chairs-row bottom">
                <div
                  v-for="n in getSillasBottom(mesa.capacidad)"
                  :key="'b' + n"
                  class="chair"
                ></div>
              </div>

              <div
                v-if="
                  !modoEditar &&
                  mesa.pedido_id &&
                  mesa.estado === 2 &&
                  mesa.pedido_estado
                "
                class="estado-badge"
                :class="getEstadoPedidoClass(mesa.pedido_estado)"
              >
                <i :class="getEstadoPedidoIcon(mesa.pedido_estado)"></i>
                {{ getEstadoPedidoTexto(mesa.pedido_estado) }}
              </div>

              <div
                v-if="mesa.estado === 2 && mesa.venta_id"
                class="boton-reabrir"
                @click.stop="confirmarReabrirMesa(mesa)"
              >
                <i class="pi pi-plus text-xs"></i>
                <span>REABRIR</span>
              </div>

              <div
                v-if="mesa.estado === 2 && mesa.venta_id"
                class="boton-liberar"
                @click.stop="confirmarLiberarMesa(mesa)"
              >
                <i class="pi pi-sign-out text-xs"></i>
                <span>LIBERAR</span>
              </div>

              <div
                v-if="!modoEditar && mesa.pedido_id && mesa.estado === 2"
                class="timer-badge"
              >
                <i class="pi pi-clock"></i>
                {{ mesa.tiempo_ocupada }}
              </div>

              <span class="capacity-text">
                <i class="pi pi-users"></i> {{ mesa.capacidad }}p
              </span>

              <div v-if="modoEditar" class="drag-handle">
                <i class="pi pi-arrows-alt"></i>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal Entregar Pedido Normal -->
    <Dialog
      v-model:visible="modalEntregarPedido"
      :style="{ width: '500px' }"
      header="Confirmar Entrega"
      :modal="true"
      class="p-fluid modal-entregar-compacto"
    >
      <div v-if="pedidoAEntregar" class="confirmacion-entrega">
        <div
          class="surface-100 border-round p-3 mb-3 flex justify-content-between align-items-center"
        >
          <div>
            <div class="text-xl font-bold text-900">
              Pedido #{{ pedidoAEntregar.id }}
            </div>
            <div class="text-sm text-600 font-medium mt-1">
              <i class="pi pi-user mr-1 text-xs"></i
              >{{ pedidoAEntregar.cliente?.nombre || "S/N" }}
            </div>
          </div>
          <div class="text-right">
            <div class="text-xs text-500 font-bold mb-1">TOTAL</div>
            <div class="text-3xl font-black text-blue-600 line-height-1">
              {{ formatCurrency(pedidoAEntregar.total) }}
            </div>
          </div>
        </div>

        <div class="border-1 surface-border border-round mb-3 overflow-hidden">
          <div
            class="bg-gray-50 p-2 text-xs font-bold text-700 border-bottom-1 surface-border"
          >
            DETALLE DE PEDIDO
          </div>
          <div class="overflow-y-auto" style="max-height: 180px">
            <table class="w-full text-sm">
              <tbody>
                <tr
                  v-for="item in pedidoAEntregar.items"
                  :key="item.id"
                  class="border-bottom-1 surface-border last:border-none"
                >
                  <td class="p-2" style="width: 30px">
                    <span
                      class="bg-bluegray-100 text-bluegray-800 font-bold px-2 py-1 border-round-md text-xs"
                    >
                      {{ item.cantidad }}
                    </span>
                  </td>
                  <td class="p-2">
                    <div class="font-medium text-900">
                      {{ item.producto?.descripcion }}
                    </div>
                    <div
                      v-if="item.nota_cocina"
                      class="text-xs text-orange-600 mt-1"
                    >
                      <i class="pi pi-comment mr-1"></i>{{ item.nota_cocina }}
                    </div>
                  </td>
                  <td
                    class="p-2 text-right font-bold text-700"
                    style="width: 90px"
                  >
                    {{ formatCurrency(item.cantidad * item.precio_unitario) }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <template #footer>
        <div class="flex gap-2 justify-content-end pt-2">
          <Button
            label="CANCELAR"
            icon="pi pi-times"
            class="p-button-text p-button-secondary p-button-sm"
            @click="cerrarModalEntregar"
          />
          <Button
            label="CONFIRMAR ENTREGA"
            icon="pi pi-check-circle"
            class="p-button-success p-button-sm font-bold shadow-2"
            @click="entregarPedido"
          />
        </div>
      </template>
    </Dialog>

    <!-- Modal Entregar Yango -->
    <Dialog
      v-model:visible="modalEntregarYango"
      :style="{ width: '500px' }"
      header="Confirmar Entrega Yango"
      :modal="true"
      class="p-fluid modal-entregar-yango"
    >
      <div v-if="pedidoAEntregar" class="confirmacion-entrega">
        <div
          class="surface-100 border-round p-3 mb-3 flex justify-content-between align-items-center"
        >
          <div>
            <div
              class="text-xl font-bold text-900 flex align-items-center gap-2"
            >
              <i class="pi pi-car text-red-600"></i>
              Pedido Yango #{{ pedidoAEntregar.id }}
            </div>
            <div class="text-sm text-600 font-medium mt-1">
              <i class="pi pi-user mr-1 text-xs"></i
              >{{ pedidoAEntregar.cliente?.nombre || "S/N" }}
            </div>
          </div>
          <div class="text-right">
            <div class="text-xs text-500 font-bold mb-1">TOTAL</div>
            <div class="text-3xl font-black text-red-600 line-height-1">
              {{ formatCurrency(pedidoAEntregar.total) }}
            </div>
          </div>
        </div>

        <div class="field mb-3">
          <label class="font-bold text-900 mb-2 block">
            NÚMERO DE PEDIDO YANGO <span class="text-red-500">*</span>
          </label>
          <InputText
            v-model="numeroPedidoYango"
            placeholder="Ej: YNG-12345"
            class="w-full p-inputtext-lg"
            :class="{ 'p-invalid': !numeroPedidoYango }"
            autofocus
          />
          <small v-if="!numeroPedidoYango" class="p-error">
            Campo obligatorio para Yango
          </small>
        </div>

        <div class="border-1 surface-border border-round overflow-hidden">
          <div
            class="bg-gray-50 p-2 text-xs font-bold text-700 border-bottom-1 surface-border"
          >
            DETALLE DE PEDIDO
          </div>
          <div class="overflow-y-auto" style="max-height: 180px">
            <table class="w-full text-sm">
              <tbody>
                <tr
                  v-for="item in pedidoAEntregar.items"
                  :key="item.id"
                  class="border-bottom-1 surface-border last:border-none"
                >
                  <td class="p-2" style="width: 30px">
                    <span
                      class="bg-bluegray-100 text-bluegray-800 font-bold px-2 py-1 border-round-md text-xs"
                    >
                      {{ item.cantidad }}
                    </span>
                  </td>
                  <td class="p-2">
                    <div class="font-medium text-900">
                      {{ item.producto?.descripcion }}
                    </div>
                  </td>
                  <td
                    class="p-2 text-right font-bold text-700"
                    style="width: 90px"
                  >
                    {{ formatCurrency(item.cantidad * item.precio_unitario) }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <template #footer>
        <div class="flex gap-2 justify-content-end pt-2">
          <Button
            label="CANCELAR"
            icon="pi pi-times"
            class="p-button-text p-button-secondary p-button-sm"
            @click="cerrarModalEntregarYango"
          />
          <Button
            label="CONFIRMAR ENTREGA"
            icon="pi pi-check-circle"
            class="p-button-danger p-button-sm font-bold shadow-2"
            :disabled="!numeroPedidoYango"
            @click="entregarPedidoYango"
          />
        </div>
      </template>
    </Dialog>
  </div>
</template>

<script>
import RestauranteService from "@/service/RestauranteService";
import interact from "interactjs";
import { useAuth } from "@/stores";

export default {
  name: "PlanoMesasVisual",
  data() {
    return {
      cajaAbierta: true, // NUEVO: controla si hay caja abierta
      mesas: [],
      mesasOriginales: [],
      pedidosParaLlevar: [],
      estadisticas: { disponibles: 0, ocupadas: 0 },
      cargando: false,
      modoEditar: false,
      guardando: false,
      omitirCocina: false,
      interactInstance: null,
      modalEntregarPedido: false,
      modalEntregarYango: false,
      pedidoAEntregar: null,
      numeroPedidoYango: "",
      modo_venta_rapida: false,
      verificando: true,
    };
  },
  computed: {
    zonasUnicas() {
      const zonas = [...new Set(this.mesas.map((m) => m.zona).filter(Boolean))];
      return zonas.sort((a, b) => {
        const mesasZonaA = this.mesas.filter((m) => m.zona === a);
        const mesasZonaB = this.mesas.filter((m) => m.zona === b);
        const minNumeroA = Math.min(
          ...mesasZonaA.map((m) => parseInt(m.numero) || 9999),
        );
        const minNumeroB = Math.min(
          ...mesasZonaB.map((m) => parseInt(m.numero) || 9999),
        );
        return minNumeroA - minNumeroB;
      });
    },
    estadisticasParaLlevar() {
      return {
        paraLlevar: this.pedidosParaLlevar.filter(
          (p) => p.tipo === "para_llevar",
        ).length,
        yango: this.pedidosParaLlevar.filter(
          (p) => p.tipo === "yango" || p.es_yango,
        ).length,
        total: this.pedidosParaLlevar.length,
      };
    },
    pedidosYango() {
      return this.pedidosParaLlevar.filter(
        (p) => p.tipo === "yango" || p.es_yango,
      );
    },
  },
  created() {
    this.restauranteService = new RestauranteService();
    this.auth = useAuth();
    // Instanciar CajaService aquí para no hacer import dinámico en mounted
    import("@/service/CajaService").then((m) => {
      this._cajaService = new m.default();
    });
  },
  async mounted() {
    // Esperar a que CajaService esté listo (por si el import aún no terminó)
    if (!this._cajaService) {
      await import("@/service/CajaService").then((m) => {
        this._cajaService = new m.default();
      });
    }

    try {
      const response = await this._cajaService.verificarCajaAbierta();

      // Si no hay caja abierta, mostrar pantalla de bloqueo
      if (!response.caja || response.caja.length === 0) {
        this.cajaAbierta = false;
        this.verificando = false;
        return;
      }

      this.modo_venta_rapida = response.modo_venta_rapida || false;
      if (this.modo_venta_rapida) {
        this.$router.replace({ name: "restaurante_venta_rapida" });
        return;
      }
    } catch (e) {
      console.error(e);
    }

    this.verificando = false;
    this.cargarTodo();
    this._intervalo = setInterval(() => {
      if (!this.modoEditar) this.cargarTodo();
    }, 15000);
  },
  beforeUnmount() {
    if (this._intervalo) {
      clearInterval(this._intervalo);
    }
    if (this.interactInstance) {
      this.interactInstance.unset();
    }
  },
  methods: {
    getTooltipItems(mesa) {
      if (
        !mesa.pedido_id ||
        mesa.estado !== 2 ||
        !mesa.pedido_items ||
        mesa.pedido_items.length === 0
      ) {
        return null;
      }
      return mesa.pedido_items
        .map(
          (item) =>
            `${item.cantidad}x ${
              item.descripcion || item.producto?.descripcion || "Producto"
            }`,
        )
        .join("\n");
    },

    getResumenProductos(items) {
      if (!items || items.length === 0) return "Sin productos";
      return items
        .map((i) => `${i.cantidad}x ${i.producto?.descripcion || "Producto"}`)
        .join("\n");
    },

    confirmarEntregar(pedido) {
      this.pedidoAEntregar = pedido;
      this.numeroPedidoYango = "";
      this.modalEntregarPedido = true;
    },

    confirmarEntregarYango(pedido) {
      this.pedidoAEntregar = pedido;
      this.numeroPedidoYango = "";
      this.modalEntregarYango = true;
    },

    cerrarModalEntregar() {
      this.modalEntregarPedido = false;
      this.pedidoAEntregar = null;
    },

    cerrarModalEntregarYango() {
      this.modalEntregarYango = false;
      this.pedidoAEntregar = null;
      this.numeroPedidoYango = "";
    },

    async entregarPedido() {
      if (!this.pedidoAEntregar) return;
      try {
        await this.restauranteService.marcarEntregado(this.pedidoAEntregar.id, {
          es_yango: false,
        });
        this.$toast.add({
          severity: "success",
          summary: "Entregado",
          detail: `Pedido #${this.pedidoAEntregar.id} entregado`,
          life: 1500,
        });
        this.cerrarModalEntregar();
        await this.cargarPedidosParaLlevar();
      } catch (error) {
        console.error(error);
        this.$toast.add({
          severity: "error",
          summary: "Error",
          detail: "No se pudo marcar como entregado",
          life: 3000,
        });
      }
    },

    async entregarPedidoYango() {
      if (!this.pedidoAEntregar || !this.numeroPedidoYango) {
        this.$toast.add({
          severity: "warn",
          summary: "Campo requerido",
          detail: "Ingresa el número de pedido Yango",
          life: 3000,
        });
        return;
      }
      try {
        await this.restauranteService.marcarEntregado(this.pedidoAEntregar.id, {
          es_yango: true,
          numero_pedido_yango: this.numeroPedidoYango,
        });
        this.$toast.add({
          severity: "success",
          summary: "Entregado",
          detail: `Pedido Yango #${this.pedidoAEntregar.id} entregado`,
          life: 1500,
        });
        this.cerrarModalEntregarYango();
        await this.cargarPedidosParaLlevar();
      } catch (error) {
        console.error(error);
        this.$toast.add({
          severity: "error",
          summary: "Error",
          detail:
            error.response?.data?.mensaje || "No se pudo marcar como entregado",
          life: 3000,
        });
      }
    },

    abrirPedidoParaLlevar(pedido) {
      this.$router.push({
        name: "restaurante_editar_pedido",
        params: { pedido_id: pedido.id },
      });
    },

    getTicketClass(estado) {
      if (estado === 1) return "ticket-espera";
      if (estado === 2) return "ticket-preparando";
      if (estado >= 3) return "ticket-listo";
      return "";
    },

    getEstadoTexto(estado) {
      if (estado === 1) return "Espera";
      if (estado === 2) return "Preparando";
      if (estado >= 3) return "¡Listo!";
      return "Desc.";
    },

    getEstadoSeverity(estado) {
      if (estado === 1) return "warning";
      if (estado === 2) return "info";
      if (estado >= 3) return "success";
      return "secondary";
    },

    tiempoTranscurrido(fecha) {
      const diff = Math.floor((Date.now() - new Date(fecha)) / 60000);
      if (diff < 1) return "ahora";
      if (diff < 60) return `${diff}m`;
      const horas = Math.floor(diff / 60);
      const mins = diff % 60;
      return `${horas}h ${mins}m`;
    },

    getSillasTop(capacidad) {
      return Math.ceil(capacidad / 2);
    },
    getSillasBottom(capacidad) {
      return Math.floor(capacidad / 2);
    },
    mesasPorZona(zona) {
      return this.mesas.filter((m) => m.zona === zona);
    },
    getDefaultXZona(mesa, zona) {
      const mesasZona = this.mesasPorZona(zona);
      const index = mesasZona.findIndex((m) => m.id === mesa.id);
      return 50 + (index % 6) * 180;
    },
    getDefaultYZona(mesa, zona) {
      const mesasZona = this.mesasPorZona(zona);
      const index = mesasZona.findIndex((m) => m.id === mesa.id);
      return 50 + Math.floor(index / 6) * 200;
    },
    async cargarTodo() {
      this.cargando = true;
      await Promise.all([this.cargarMesas(), this.cargarPedidosParaLlevar()]);
      this.cargando = false;
    },
    async cargarMesas() {
      try {
        const response = await this.restauranteService.getMesas();
        if (response.status === 200) {
          this.mesas = response.data || [];
          this.omitirCocina = response.omitir_cocina || false;
          this.calcularEstadisticas();
        }
      } catch (error) {
        console.error(error);
      }
    },
    async cargarPedidosParaLlevar() {
      try {
        const response =
          await this.restauranteService.listarPedidosParaLlevar();
        if (response.status === 200) {
          this.pedidosParaLlevar = (response.pedidos || []).filter(
            (p) => p.estado !== 4 && p.estado !== 0,
          );
        }
      } catch (error) {
        console.error("Error cargar para llevar:", error);
      }
    },
    calcularEstadisticas() {
      this.estadisticas = {
        disponibles: this.mesas.filter((m) => m.estado === 1).length,
        ocupadas: this.mesas.filter((m) => m.estado === 2).length,
      };
    },
    activarModoEditar() {
      this.modoEditar = true;
      this.mesasOriginales = JSON.parse(JSON.stringify(this.mesas));
      this.$nextTick(() => {
        this.initInteract();
      });
      this.$toast.add({
        severity: "info",
        summary: "Modo Edición Activado",
        detail: "Arrastra las mesas para reorganizarlas",
        life: 3000,
      });
    },
    initInteract() {
      this.interactInstance = interact(".mesa-item.draggable").draggable({
        inertia: false,
        modifiers: [
          interact.modifiers.restrictRect({
            restriction: "parent",
            endOnly: true,
          }),
        ],
        autoScroll: true,
        listeners: {
          move: this.dragMoveListener,
        },
      });
    },
    dragMoveListener(event) {
      const target = event.target;
      const mesaId = parseInt(target.getAttribute("data-mesa-id"));
      const mesa = this.mesas.find((m) => m.id === mesaId);
      if (!mesa) return;
      const currentX =
        parseFloat(target.style.left) ||
        mesa.posicion_x ||
        this.getDefaultXZona(mesa, mesa.zona);
      const currentY =
        parseFloat(target.style.top) ||
        mesa.posicion_y ||
        this.getDefaultYZona(mesa, mesa.zona);
      const newX = currentX + event.dx;
      const newY = currentY + event.dy;
      target.style.left = newX + "px";
      target.style.top = newY + "px";
      mesa.posicion_x = newX;
      mesa.posicion_y = newY;
    },
    cancelarEdicion() {
      this.modoEditar = false;
      this.mesas = JSON.parse(JSON.stringify(this.mesasOriginales));
      if (this.interactInstance) {
        this.interactInstance.unset();
      }
      this.$toast.add({
        severity: "info",
        summary: "Cambios Descartados",
        life: 2000,
      });
    },
    async guardarPosiciones() {
      this.guardando = true;
      try {
        const mesasParaGuardar = this.mesas.map((mesa) => ({
          id: mesa.id,
          posicion_x: mesa.posicion_x || this.getDefaultXZona(mesa, mesa.zona),
          posicion_y: mesa.posicion_y || this.getDefaultYZona(mesa, mesa.zona),
        }));
        const response = await this.restauranteService.guardarPosicionesVisual({
          mesas: mesasParaGuardar,
        });
        if (response.status === 200) {
          this.$toast.add({
            severity: "success",
            summary: "Layout Guardado",
            detail: "Las posiciones se guardaron correctamente",
            life: 3000,
          });
          this.modoEditar = false;
          if (this.interactInstance) {
            this.interactInstance.unset();
          }
          await this.cargarMesas();
        }
      } catch (error) {
        console.error(error);
        this.$toast.add({
          severity: "error",
          summary: "Error",
          detail: "No se pudieron guardar las posiciones",
          life: 3000,
        });
      } finally {
        this.guardando = false;
      }
    },
    accionMesa(mesa) {
      if (mesa.estado === 1) {
        this.$router.push({
          name: "restaurante_nuevo_pedido",
          params: { mesa_id: mesa.id },
        });
      } else if (mesa.estado === 2) {
        const pedidoId = mesa.pedido_id;
        if (pedidoId) {
          if (mesa.venta_id) {
            this.$toast.add({
              severity: "info",
              summary: "Mesa Pagada",
              detail:
                "Usa 'Reabrir' para agregar items o 'Liberar' cuando el cliente se retire",
              life: 3000,
            });
          } else {
            this.$router.push({
              name: "restaurante_editar_pedido",
              params: { pedido_id: pedidoId },
            });
          }
        }
      }
    },
    confirmarReabrirMesa(mesa) {
      this.$confirm.require({
        message: `¿Reabrir Mesa ${mesa.numero} para agregar más items?`,
        header: "Reabrir Mesa",
        icon: "pi pi-plus-circle",
        acceptClass: "p-button-info",
        acceptLabel: "Sí, Reabrir",
        rejectLabel: "Cancelar",
        accept: () => this.reabrirMesa(mesa),
      });
    },
    async reabrirMesa(mesa) {
      try {
        const response = await this.restauranteService.reabrirMesa(mesa.id);
        if (response.status === 200) {
          this.$toast.add({
            severity: "success",
            summary: "Mesa Reabierta",
            detail: response.mensaje,
            life: 2000,
          });
          this.$router.push({
            name: "restaurante_editar_pedido",
            params: { pedido_id: response.pedido.id },
          });
        }
      } catch (error) {
        console.error(error);
        this.$toast.add({
          severity: "error",
          summary: "Error",
          detail: "No se pudo reabrir la mesa",
          life: 3000,
        });
      }
    },
    confirmarLiberarMesa(mesa) {
      this.$confirm.require({
        message: `¿El cliente ya se retiró de la Mesa ${mesa.numero}?`,
        header: "Liberar Mesa",
        icon: "pi pi-sign-out",
        acceptClass: "p-button-info",
        acceptLabel: "Sí, liberar",
        rejectLabel: "Cancelar",
        accept: () => this.liberarMesa(mesa),
      });
    },
    async liberarMesa(mesa) {
      try {
        await this.restauranteService.liberarMesa(mesa.id);
        this.$toast.add({
          severity: "success",
          summary: "Mesa Liberada",
          detail: `Mesa ${mesa.numero} disponible`,
          life: 2000,
        });
        await this.cargarMesas();
      } catch (error) {
        console.error(error);
        this.$toast.add({
          severity: "error",
          summary: "Error",
          detail: "No se pudo liberar la mesa",
          life: 3000,
        });
      }
    },
    getEstadoClass(estado) {
      return (
        { 1: "mesa-libre", 2: "mesa-ocupada", 3: "mesa-reservada" }[estado] ||
        ""
      );
    },
    getEstadoPedidoIcon(estado) {
      const iconos = {
        1: "pi pi-clock",
        2: "pi pi-spin pi-spinner",
        3: "pi pi-check-circle",
      };
      return iconos[estado] || "pi pi-circle";
    },
    getEstadoPedidoTexto(estado) {
      const estados = { 1: "Pendiente", 3: "Listo", 5: "Cerrado" };
      return estados[estado] || "Desconocido";
    },
    getEstadoPedidoClass(estado) {
      const clases = {
        1: "estado-pendiente",
        3: "estado-listo",
        5: "estado-cerrado",
      };
      return clases[estado] || "";
    },
    formatCurrency(value) {
      return new Intl.NumberFormat("es-BO", {
        minimumFractionDigits: 2,
      }).format(value || 0);
    },
  },
};
</script>

<style scoped>
.plano-visual-container {
  min-height: 100vh;
  background: #f8fafc;
  display: flex;
  flex-direction: column;
}

.plano-header {
  background: white;
  padding: 1.5rem 2rem;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;
  position: sticky;
  top: 0;
  z-index: 100;
}

.header-left {
  flex: 1;
}

.plano-title {
  font-size: 1.75rem;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 0.5rem 0;
}

.estadisticas {
  display: flex;
  gap: 1.5rem;
  font-size: 0.875rem;
  color: #64748b;
  flex-wrap: wrap;
}

.stat-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.stat-dot {
  width: 0.75rem;
  height: 0.75rem;
  border-radius: 50%;
}

.stat-libre {
  background: #10b981;
}

.stat-ocupada {
  background: #ef4444;
}

.stat-para-llevar {
  background: #f59e0b;
}

.stat-yango {
  background: #dc2626;
}

.header-right {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.btn-takeout {
  background: linear-gradient(135deg, #ffc107 0%, #ff9800 100%);
  border: none;
  border-radius: 2rem;
  padding: 0.75rem 1rem;
  position: relative;
  overflow: hidden;
}

.btn-takeout::after {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(rgba(255, 255, 255, 0.2), rgba(255, 255, 255, 0));
  opacity: 0;
  transition: opacity 0.3s;
}

.btn-takeout:hover::after {
  opacity: 1;
}

.btn-yango {
  background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
  border: none;
  border-radius: 2rem;
  padding: 0.75rem 1rem;
  position: relative;
  overflow: hidden;
}

.btn-yango::after {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(rgba(255, 255, 255, 0.2), rgba(255, 255, 255, 0));
  opacity: 0;
  transition: opacity 0.3s;
}

.btn-yango:hover::after {
  opacity: 1;
}

.modo-edicion-banner {
  background: #fef3c7;
  border-bottom: 2px solid #fbbf24;
  padding: 0.75rem 2rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #92400e;
  font-weight: 600;
  font-size: 0.875rem;
}

.seccion-para-llevar,
.seccion-yango {
  padding: 1.5rem 2rem;
  background: white;
  border-bottom: 1px solid #e2e8f0;
}

.seccion-titulo {
  font-size: 1.25rem;
  font-weight: 700;
  color: #f59e0b;
  margin: 0 0 1rem 0;
  padding-left: 0.75rem;
  border-left: 4px solid #f59e0b;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.seccion-titulo-yango {
  color: #dc2626;
  border-left-color: #dc2626;
}

.tickets-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 1rem;
}

.ticket-compacto {
  background: white;
  border-radius: 10px;
  padding: 0.75rem;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
  border-left: 3px solid;
  transition: all 0.2s;
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  cursor: pointer;
}

.ticket-compacto:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.12);
}

.ticket-espera {
  border-left-color: #f59e0b;
  background: linear-gradient(135deg, #fffbeb 0%, white 100%);
}

.ticket-preparando {
  border-left-color: #3b82f6;
  background: linear-gradient(135deg, #eff6ff 0%, white 100%);
}

.ticket-listo {
  border-left-color: #10b981;
  background: linear-gradient(135deg, #f0fdf4 0%, white 100%);
  border: 2px solid #10b981;
  animation: pulse-listo 2s ease-in-out infinite;
}

.ticket-yango {
  border-left-color: #dc2626 !important;
  background: linear-gradient(135deg, #fef2f2 0%, white 100%) !important;
}

.ticket-yango .ticket-numero {
  color: #dc2626;
}

@keyframes pulse-listo {
  0%,
  100% {
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
  }
  50% {
    box-shadow: 0 4px 12px rgba(16, 185, 129, 0.25);
  }
}

.ticket-top {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.ticket-numero {
  font-size: 1.25rem;
  font-weight: 800;
  color: #1e293b;
}

.ticket-info {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.ticket-monto {
  font-size: 1.15rem;
  font-weight: 800;
  color: #0f172a;
}

.ticket-meta {
  display: flex;
  gap: 0.75rem;
  font-size: 0.7rem;
  color: #64748b;
}

.ticket-cliente {
  font-size: 0.75rem;
  color: #475569;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  padding-top: 0.5rem;
  border-top: 1px solid #f1f5f9;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.ticket-action {
  margin-top: 0.5rem;
  display: flex;
  justify-content: center;
}

.plano-canvas {
  flex: 1;
  padding: 2rem;
  background: #0f172a;
  overflow: auto;
}

.zona-section {
  margin-bottom: 2rem;
  border: 2px dashed rgba(148, 163, 184, 0.3);
  border-radius: 8px;
  padding: 1rem;
  background: rgba(30, 41, 59, 0.3);
}

.zona-header {
  background: rgba(148, 163, 184, 0.2);
  color: #e2e8f0;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  font-weight: 700;
  font-size: 1.1rem;
  margin-bottom: 1rem;
  text-transform: uppercase;
  letter-spacing: 1px;
}

.zona-canvas {
  position: relative;
  min-height: 400px;
  background: rgba(15, 23, 42, 0.5);
  border-radius: 6px;
  padding: 1rem;
  background-image: linear-gradient(
      rgba(148, 163, 184, 0.1) 1px,
      transparent 1px
    ),
    linear-gradient(90deg, rgba(148, 163, 184, 0.1) 1px, transparent 1px);
  background-size: 50px 50px;
}

.mesa-item {
  position: absolute;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0.5rem;
  transition: transform 0.2s;
}

.mesa-item.draggable {
  cursor: move;
}

.mesa-item.draggable:hover {
  transform: scale(1.05);
}

.table-body {
  width: 130px;
  height: 90px;
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.2), inset 0 -4px 0 rgba(0, 0, 0, 0.05);
  border: 2px solid #e2e8f0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: relative;
  z-index: 2;
  transition: all 0.3s;
}

.chairs-row {
  display: flex;
  gap: 12px;
  z-index: 1;
}

.chairs-row.top {
  margin-bottom: -12px;
}

.chairs-row.bottom {
  margin-top: -12px;
}

.chair {
  width: 30px;
  height: 18px;
  border-radius: 8px;
  background-color: #f1f5f9;
  border: 1px solid #cbd5e1;
}

.mesa-libre .table-body {
  border-color: #86efac;
  background: #f0fdf4;
}

.mesa-libre .chair {
  background: #ffffff;
  border-color: #86efac;
}

.mesa-ocupada .table-body {
  background: #fef2f2;
  border-color: #fca5a5;
  box-shadow: 0 8px 15px rgba(239, 68, 68, 0.3);
}

.mesa-ocupada .chair {
  background-color: #f87171;
  border-color: #ef4444;
}

.mesa-numero {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1e293b;
  z-index: 1;
}

.mesa-icon-ocupada {
  position: absolute;
  font-size: 3rem;
  color: #ef4444;
  opacity: 0.1;
}

.pedido-badge {
  position: absolute;
  top: 8px;
  background: #000000;
  color: white;
  padding: 2px 10px;
  border-radius: 12px;
  font-size: 0.875rem;
  font-weight: 700;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
}

.price-tag {
  position: absolute;
  bottom: 8px;
  background: rgba(255, 255, 255, 0.95);
  color: #ef4444;
  font-weight: 700;
  font-size: 1rem;
  padding: 2px 8px;
  border-radius: 6px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.timer-badge {
  position: absolute;
  top: 5px;
  right: 5px;
  background: #1e293b;
  color: white;
  font-size: 0.7rem;
  padding: 2px 6px;
  border-radius: 4px;
  font-weight: 600;
  z-index: 10;
}

.estado-badge {
  position: absolute;
  top: 5px;
  left: 5px;
  font-size: 0.65rem;
  padding: 3px 8px;
  border-radius: 12px;
  font-weight: 700;
  z-index: 10;
  text-transform: uppercase;
}

.estado-pendiente {
  background: #fef3c7;
  color: #b45309;
}

.estado-listo {
  background: #dcfce7;
  color: #16a34a;
}

.boton-reabrir {
  position: absolute;
  bottom: -10px;
  left: 0;
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: white;
  font-size: 0.7rem;
  padding: 5px 10px;
  border-radius: 8px;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  box-shadow: 0 2px 8px rgba(59, 130, 246, 0.4);
  transition: all 0.2s;
  z-index: 15;
}

.boton-reabrir:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.6);
}

.boton-liberar {
  position: absolute;
  bottom: -10px;
  right: 0;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
  font-size: 0.7rem;
  padding: 5px 10px;
  border-radius: 8px;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  box-shadow: 0 2px 8px rgba(16, 185, 129, 0.4);
  transition: all 0.2s;
  z-index: 15;
}

.boton-liberar:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.6);
}

.drag-handle {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: rgba(0, 0, 0, 0.7);
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
  color: white;
  font-size: 1.2rem;
}

.capacity-text {
  color: #94a3b8;
  font-size: 0.75rem;
  font-weight: 600;
  margin-top: 0.5rem;
  z-index: 5;
}

.modal-entregar-compacto :deep(.p-dialog-content),
.modal-entregar-yango :deep(.p-dialog-content) {
  padding: 1rem;
  overflow: visible;
}

.modal-entregar-compacto :deep(.p-dialog-footer),
.modal-entregar-yango :deep(.p-dialog-footer) {
  padding: 0.75rem 1rem;
  border-top: 1px solid #e5e7eb;
}

.confirmacion-entrega {
  font-size: 0.9rem;
}

@media (max-width: 768px) {
  .plano-header {
    padding: 1rem;
  }

  .plano-title {
    font-size: 1.25rem;
  }

  .estadisticas {
    font-size: 0.75rem;
    gap: 1rem;
  }

  .header-right {
    width: 100%;
    justify-content: flex-start;
  }

  .tickets-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 0.75rem;
  }

  .plano-canvas {
    padding: 0.75rem;
  }

  .zona-section {
    padding: 0.5rem;
    margin-bottom: 1rem;
  }

  .zona-header {
    font-size: 0.9rem;
    padding: 0.4rem 0.75rem;
    margin-bottom: 0.75rem;
  }

  .zona-canvas {
    display: grid !important;
    grid-template-columns: repeat(2, 1fr) !important;
    gap: 1rem;
    padding: 0.5rem;
    min-height: auto;
  }

  .mesa-item {
    position: relative !important;
    left: auto !important;
    top: auto !important;
    transform: none !important;
  }

  .table-body {
    width: 100%;
    max-width: 140px;
    height: 95px;
  }

  .mesa-numero {
    font-size: 1.4rem;
  }

  .chairs-row {
    gap: 10px;
  }

  .chair {
    width: 28px;
    height: 16px;
  }

  .pedido-badge {
    font-size: 0.75rem;
    padding: 2px 8px;
    top: 6px;
  }

  .price-tag {
    font-size: 0.9rem;
    padding: 2px 6px;
    bottom: 6px;
  }

  .timer-badge {
    font-size: 0.65rem;
    padding: 2px 5px;
  }

  .estado-badge {
    font-size: 0.6rem;
    padding: 2px 6px;
  }

  .boton-reabrir,
  .boton-liberar {
    font-size: 0.65rem;
    padding: 4px 8px;
    bottom: -8px;
  }

  .capacity-text {
    font-size: 0.7rem;
    margin-top: 0.35rem;
  }

  .seccion-para-llevar,
  .seccion-yango {
    padding: 1rem;
  }

  .seccion-titulo {
    font-size: 1rem;
  }

  .mesa-icon-ocupada {
    font-size: 2.5rem;
  }

  .drag-handle {
    width: 35px;
    height: 35px;
    font-size: 1rem;
  }
}

@media (max-width: 480px) {
  .zona-canvas {
    gap: 0.75rem;
    padding: 0.35rem;
  }

  .table-body {
    max-width: 130px;
    height: 90px;
  }

  .mesa-numero {
    font-size: 1.3rem;
  }

  .chairs-row {
    gap: 8px;
  }

  .chair {
    width: 26px;
    height: 15px;
  }

  .boton-reabrir,
  .boton-liberar {
    font-size: 0.6rem;
    padding: 3px 6px;
  }
}
</style>